import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ListPlus, Mail, Pencil, Send, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { DanishCalendarField } from '@/components/ui/DanishCalendarField';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { Select } from '@/components/ui/Select';
import { SlideOver, SlideOverFooter } from '@/components/ui/SlideOver';
import { QuestionnaireListsDialog } from '@/components/questionnaires/QuestionnaireListsDialog';
import { QuestionnairePickerCatalog } from '@/components/questionnaires/QuestionnairePickerCatalog';
import { QuestionnaireTagChips } from '@/components/questionnaires/QuestionnaireTagFilter';
import { useIsDesktopNav } from '@/hooks/useNavBreakpoint';
import type { QuestionnaireList } from '@/hooks/useQuestionnaireLibraryPrefs';
import {
  useQuestionnaireFavorites,
  useQuestionnaireLists,
} from '@/hooks/useQuestionnaireLibraryPrefs';
import type { PatientAppointmentItem } from '@/hooks/usePatientAppointments';
import type { QuestionnairePickerItem } from '@/lib/questionnairePickerLabel';
import { addCivilDays, formatClinicDateTime, todayCivil } from '@/lib/questionnaireDateFormat';
import { maskPatientEmail } from '@/lib/questionnaireEmailMask';
import { cn } from '@/utils/cn';

export type QuestionnairePickerConfirmPayload = {
  ids: string[];
  dueAt: string | null;
  locale: 'da-DK' | 'en-DK';
  appointmentId: string | null;
};

function sameQuestionnaireIds(left: readonly string[], right: readonly string[]): boolean {
  if (left.length !== right.length) return false;
  const seen = new Set(left);
  return right.every((id) => seen.has(id));
}

export function QuestionnairePicker({
  open,
  title,
  subtitle,
  items,
  lockedIds,
  pendingIds,
  recentIds,
  showSearch = true,
  showLists,
  showFavorites,
  showRecent,
  note,
  clinicId,
  mode = 'select',
  presentation = 'slide-over',
  confirmLabel,
  patientName,
  patientEmail,
  appointments,
  defaultLocale = 'da-DK',
  defaultDueDays = 7,
  sending = false,
  tenantReady = true,
  tenantWarning,
  initialSelectedIds,
  onClose,
  onConfirm,
}: {
  open: boolean;
  title: string;
  subtitle?: string;
  items: QuestionnairePickerItem[];
  lockedIds?: readonly string[];
  pendingIds?: readonly string[];
  recentIds?: readonly string[];
  showSearch?: boolean;
  showLists?: boolean;
  showFavorites?: boolean;
  showRecent?: boolean;
  note?: { title: string; body: string } | null;
  clinicId: string | null | undefined;
  mode?: 'select' | 'send';
  presentation?: 'slide-over' | 'popover';
  confirmLabel?: string;
  patientName?: string;
  patientEmail?: string | null;
  appointments?: readonly PatientAppointmentItem[];
  defaultLocale?: 'da-DK' | 'en-DK';
  defaultDueDays?: number;
  sending?: boolean;
  tenantReady?: boolean;
  tenantWarning?: string;
  initialSelectedIds?: readonly string[];
  onClose: () => void;
  onConfirm: (payload: QuestionnairePickerConfirmPayload) => void;
}) {
  const { t, i18n } = useTranslation();
  const desktop = useIsDesktopNav();
  const listsEnabled = showLists ?? mode === 'send';
  const favoritesEnabled = showFavorites ?? true;
  const recentEnabled = showRecent ?? Boolean(recentIds?.length);
  const favorites = useQuestionnaireFavorites(clinicId);
  const lists = useQuestionnaireLists(clinicId);
  const [query, setQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [picked, setPicked] = useState<string[]>([]);
  const [fromListName, setFromListName] = useState<string | null>(null);
  const [step, setStep] = useState<'catalog' | 'ready'>('catalog');
  const [dueAt, setDueAt] = useState('');
  const [locale, setLocale] = useState<'da-DK' | 'en-DK'>(defaultLocale);
  const [appointmentId, setAppointmentId] = useState<string | null>(null);
  const [listsOpen, setListsOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setSelectedTags([]);
      setPicked([]);
      setFromListName(null);
      setStep('catalog');
      setDueAt('');
      setAppointmentId(null);
      setListsOpen(false);
      return;
    }
    setPicked(initialSelectedIds ? [...initialSelectedIds] : []);
    setLocale(defaultLocale);
    setDueAt(addCivilDays(todayCivil(), defaultDueDays));
  }, [defaultDueDays, defaultLocale, initialSelectedIds, open]);

  useEffect(() => {
    if (!open || presentation !== 'popover' || !desktop) return;
    function onPointer(event: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) onClose();
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    }
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [desktop, onClose, open, presentation]);

  const upcoming = useMemo(
    () =>
      (appointments ?? []).filter(
        (row) => row.status !== 'cancelled' && new Date(row.starts_at).getTime() >= Date.now() - 60_000,
      ),
    [appointments],
  );
  const selectedAppointment = upcoming.find((row) => row.id === appointmentId) ?? null;
  const markedDates = upcoming.map((row) => row.starts_at.slice(0, 10));

  function toggle(id: string) {
    if ((lockedIds ?? []).includes(id)) return;
    setFromListName(null);
    setPicked((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));
  }

  function toggleList(list: QuestionnaireList) {
    const ids = list.templateIds.filter((id) => items.some((item) => item.id === id));
    const allOn = ids.length > 0 && ids.every((id) => picked.includes(id));
    setPicked(allOn ? picked.filter((id) => !ids.includes(id)) : [...new Set([...picked, ...ids])]);
    setFromListName(allOn ? null : list.name);
    if (!allOn && mode === 'send') setStep('ready');
  }

  function confirm() {
    onConfirm({
      ids: picked,
      dueAt: dueAt || null,
      locale,
      appointmentId,
    });
  }

  if (!open) return null;

  const catalog = (
    <QuestionnairePickerCatalog
      items={items}
      selectedIds={picked}
      onToggle={toggle}
      lockedIds={lockedIds}
      pendingIds={pendingIds}
      favoriteIds={favorites.data ?? []}
      onToggleFavorite={favoritesEnabled ? (id) => favorites.toggle.mutate(id) : undefined}
      lists={lists.data ?? []}
      onToggleList={toggleList}
      recentIds={recentIds}
      query={query}
      onQueryChange={setQuery}
      selectedTags={selectedTags}
      onTagsChange={setSelectedTags}
      showSearch={showSearch}
      showLists={listsEnabled}
      showFavorites={favoritesEnabled}
      showRecent={recentEnabled}
      note={note}
      onEditLists={listsEnabled ? () => setListsOpen(true) : undefined}
    />
  );

  const sendOptions = (
    <>
      {step === 'catalog' && picked.length > 0 ? (
        <p className="pk-sec">
          <button
            type="button"
            className="lnk"
            onClick={() => {
              void lists.createList.mutateAsync({
                name: fromListName || t('forms.lists.default_name', 'Ny liste'),
                templateIds: picked,
              });
              setListsOpen(true);
            }}
          >
            <ListPlus className="i sm" aria-hidden />
            {t('forms.picker.save_as_list', 'Gem valget som liste')}
          </button>
        </p>
      ) : null}
      <DanishCalendarField
        label={t('forms.picker.due', 'Frist')}
        value={dueAt}
        onChange={setDueAt}
        markedDates={markedDates}
        markedHint={
          selectedAppointment
            ? t('forms.picker.appointment_dot', {
                when: formatClinicDateTime(selectedAppointment.starts_at, i18n.language),
                defaultValue: `Patientens aftale ${formatClinicDateTime(selectedAppointment.starts_at, i18n.language)}`,
              })
            : upcoming[0]
              ? t('forms.picker.appointment_dot', {
                  when: formatClinicDateTime(upcoming[0].starts_at, i18n.language),
                  defaultValue: `Patientens aftale ${formatClinicDateTime(upcoming[0].starts_at, i18n.language)}`,
                })
              : undefined
        }
        min={todayCivil()}
      />
      <p className="hint">{t('forms.picker.due_hint', 'Patienten får op til 2 påmindelser, hvis skemaerne ikke er udfyldt.')}</p>
      <div className="fld">
        <span className="lbl">{t('forms.picker.language', 'Sprog')}</span>
        <SegmentedControl
          ariaLabel={t('forms.picker.language', 'Sprog')}
          value={locale}
          onChange={setLocale}
          options={[
            { value: 'da-DK', label: t('forms.picker.language_da', 'Dansk') },
            { value: 'en-DK', label: t('forms.picker.language_en', 'Engelsk') },
          ]}
        />
      </div>
      <Select
        label={`${t('forms.picker.link_appointment', 'Knyt til aftale')} ${t('forms.picker.optional', '(valgfri)')}`}
        value={appointmentId ?? ''}
        onChange={(event) => setAppointmentId(event.target.value || null)}
      >
        <option value="">{t('forms.no_appointment', 'Uden aftale')}</option>
        {upcoming.map((row) => (
          <option key={row.id} value={row.id}>
            {formatClinicDateTime(row.starts_at, i18n.language)}
            {row.service_name ? ` · ${row.service_name}` : ''}
          </option>
        ))}
      </Select>
      <p className="ro">
        <Mail className="i sm" aria-hidden />
        <span>
          {maskPatientEmail(patientEmail)
            ? t('forms.picker.sent_to', {
                email: maskPatientEmail(patientEmail),
                defaultValue: `Sendes til patientportalen og ${maskPatientEmail(patientEmail)}`,
              })
            : t('forms.picker.sent_to_email', 'Sendes til patientportalen og e-mail')}
        </span>
      </p>
    </>
  );

  const ready = (
    <div>
      <div className="sel-box">
        <div className="hd">
          <span>
            <b>{t('forms.picker.selected_count', { count: picked.length, defaultValue: `${picked.length} skemaer valgt` })}</b>
            {fromListName
              ? ` · ${t('forms.picker.from_list', { name: fromListName, defaultValue: `fra listen ${fromListName}` })}`
              : null}
          </span>
          <span className="r">
            <button type="button" className="lnk" onClick={() => setStep('catalog')}>
              <Pencil className="i xs" aria-hidden />
              {t('forms.picker.change_selection', 'Ændr valg')}
            </button>
          </span>
        </div>
        <ul>
          {picked.map((id) => {
            const item = items.find((row) => row.id === id);
            if (!item) return null;
            return (
              <li key={id}>
                <span className="cb on" aria-hidden>
                  <Check />
                </span>
                <div className="nl">
                  {item.name}
                  <QuestionnaireTagChips tags={item.tags} />
                </div>
                {item.questionCount ? (
                  <span className="qn num">
                    {t('forms.picker.questions', {
                      count: item.questionCount,
                      defaultValue: `${item.questionCount} spørgsmål`,
                    })}
                  </span>
                ) : (
                  <span />
                )}
                <button
                  type="button"
                  className="sk-ib"
                  aria-label={t('forms.picker.remove', { name: item.name, defaultValue: `Fjern ${item.name}` })}
                  onClick={() => toggle(id)}
                >
                  <X className="i sm" />
                </button>
              </li>
            );
          })}
        </ul>
        <div className="ft">
          <button
            type="button"
            className="lnk"
            onClick={() => {
              void lists.createList.mutateAsync({
                name: fromListName || t('forms.lists.default_name', 'Ny liste'),
                templateIds: picked,
              });
              setListsOpen(true);
            }}
          >
            <ListPlus className="i sm" aria-hidden />
            {t('forms.picker.save_as_list', 'Gem valget som liste')}
          </button>
        </div>
      </div>
      {sendOptions}
    </div>
  );

  const catalogWithOptions = (
    <div>
      {catalog}
      {mode === 'send' && picked.length > 0 ? sendOptions : null}
    </div>
  );

  const body = (
    <div className="sk">
      {!tenantReady && tenantWarning ? (
        <p className="rounded-ctl border border-ds-amber-line bg-ds-amber-tint p-s3 text-sm text-ds-amber-ink">{tenantWarning}</p>
      ) : step === 'ready' && mode === 'send' ? (
        ready
      ) : (
        catalogWithOptions
      )}
    </div>
  );

  const footerLabel =
    confirmLabel ??
    (mode === 'send'
      ? picked.length > 0
        ? t('forms.picker.send_count', {
            count: picked.length,
            defaultValue: picked.length === 1 ? `Send ${picked.length} skema` : `Send ${picked.length} skemaer`,
          })
        : t('forms.picker.send_none', 'Send skemaer')
      : t('treatment_courses.picker_add_count', { count: picked.length }));

  const footer = (
    <>
      <span className="l">
        {picked.length === 0 ? t('forms.picker.none_selected', 'Ingen skemaer valgt') : null}
      </span>
      <Button type="button" variant="secondary" onClick={onClose}>
        {t('common.cancel')}
      </Button>
      <Button
        type="button"
        disabled={picked.length === 0 || sending || !tenantReady}
        loading={sending}
        onClick={confirm}
      >
        <Send />
        {footerLabel}
      </Button>
    </>
  );

  const listsDialog = (
    <QuestionnaireListsDialog
      open={listsOpen}
      lists={lists.data ?? []}
      items={items}
      onClose={() => setListsOpen(false)}
      onRename={(id, name) => lists.renameList.mutate({ listId: id, name })}
      onDelete={(id) => lists.deleteList.mutate(id)}
      onReorderLists={(ids) => lists.reorderLists.mutate(ids)}
      onReplaceItems={(listId, templateIds) => lists.replaceListItems.mutate({ listId, templateIds })}
      onCreate={() => {
        void lists.createList.mutateAsync({ name: t('forms.lists.default_name', 'Ny liste') });
      }}
    />
  );

  if (presentation === 'popover') {
    const sheet = !desktop;
    return (
      <>
        {sheet ? <div className="sk-scrim" onClick={onClose} /> : null}
        <div
          ref={wrapRef}
          className={cn(sheet ? 'sk-sheet sk' : 'qpick dsk sk')}
          role="dialog"
          aria-labelledby="qp-t-pop"
          data-testid="questionnaire-multi-picker"
        >
          {sheet ? <div className="handle" aria-hidden /> : null}
          <div className="qp-h">
            <div className="grow">
              <h4 id="qp-t-pop">{title}</h4>
              {subtitle ? <p className="num">{subtitle}</p> : null}
            </div>
            <button type="button" className="ibtn" aria-label={t('common.close')} onClick={onClose}>
              <X className="i sm" />
            </button>
          </div>
          {body}
          <div className="qp-f">{footer}</div>
        </div>
        {listsDialog}
      </>
    );
  }

  return (
    <>
      <SlideOver
        open={open}
        title={title}
        width={560}
        description={subtitle ?? (patientName ? t('forms.picker.subtitle', { name: patientName, defaultValue: `Til ${patientName} · du kan vælge flere` }) : undefined)}
        onClose={onClose}
        dirty={!sameQuestionnaireIds(picked, initialSelectedIds ?? [])}
        closeLabel={t('forms.responses.close', 'Luk')}
        footer={tenantReady ? <SlideOverFooter onCancel={onClose} primary={
          <Button type="button" disabled={picked.length === 0 || sending} loading={sending} onClick={confirm}>
            <Send />
            {footerLabel}
          </Button>
        } /> : undefined}
      >
        {body}
      </SlideOver>
      {listsDialog}
    </>
  );
}
